import { CommonModule } from '@angular/common';
import { Component, inject, signal, OnDestroy, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BinderCardList } from '@components/partials/binder/binder-card-list/binder-card-list';
import { BinderOverview } from '@components/partials/binder/binder-overview/binder-overview';
import { BinderPageList } from '@components/partials/binder/binder-page-list/binder-page-list';
import { LitleBinder } from '@components/partials/litle-binder/litle-binder';
import { UiModule } from '@components/ui/ui-module';
import { UserBinder, Binder as BinderM, CardBinder, Page, Scan, BinderDetails } from '@models/binder.model';
import { Card } from '@models/card.model';
import { BinderService } from '@services/binder.service';
import { Menu } from '@services/menu.service';
import { NotificationService } from '@services/notification.service';
import { CardApiResponse, PageApiResponse, ScannerService } from '@services/scanner.service';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { compile } from 'tailwindcss';

@Component({
  selector: 'lgb-binder',
  imports: [
    RouterModule,
    UiModule,
    CommonModule,
    BinderOverview,
    BinderPageList,
    BinderCardList,
    LitleBinder
  ],
  templateUrl: './binder.html',
  styleUrl: './binder.css',
})
export class Binder implements OnDestroy{
  //service
  readonly route=inject(ActivatedRoute);
  readonly menu=inject(Menu);
  readonly binderS=inject(BinderService);
  readonly scanService=inject(ScannerService);
  readonly notifService=inject(NotificationService);

  //data
  readonly binder=signal<BinderM>(null);
  readonly uBinder=signal<UserBinder>(null);
  readonly pages=signal<Page[]>([]);
  readonly cards=signal<CardBinder[]>([]);
  readonly references=signal<Card[]>([]);
  readonly scans=signal<Scan[]>([]);
  private timeout: number;
  readonly share=signal<string>(null);
  readonly isSharing=computed(() => this.share()!=null);
  readonly binderObs$=signal<Observable<BinderDetails>>(null);

  //ui
  readonly loading=signal(true);
  readonly error=signal<string>(null);
  readonly tabActive=signal('overview');
  private fragment: string;

  constructor(){
    //observe le scanService pour ajouter pages qui sont ajoutés, pour les ajoute dans la liste 
    this.scanService.resultPage
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(val => this.updatePages(val));

    //observe la supression de page
    this.scanService.resultRmPage
      .subscribe(page => {
        //supression des cartes
        this.cards.set(this.cards().filter(c => c.page!=page.ident));

        //supression des pages
        this.pages.set(this.pages().filter(p => p.ident!=page.ident));
      });

    //observe le scanService pour ajouter cartes qui sont ajoutées, pour les ajoute dans la liste 
    //pas de gestion de la modification parce que le refresh se fait depuis l'interface de la carte
    this.scanService.resultCard
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(val => this.updateCard(val));

    //récup du tab active
    this.route.queryParamMap
      .subscribe(params => {
        if(params.get('active'))
          this.tabActive.set(params.get('active'));
        this.share.set(params.get('share'));
      });

    //récup du fragment pour scroll si c'est un retour
    this.route.fragment.subscribe(fragment => this.fragment=fragment);

    //récup des datas
    this.route.paramMap
      .pipe(
        switchMap(params => {
          this.binderObs$.set(this.binderS.getDetailsUserBinder(params.get('ident')));
          if(this.isSharing())
            this.binderObs$.set(this.binderS.getDetailsSharedBinder(params.get('ident'), this.share()));
          return forkJoin({
            binders: this.binderS.getBinders(),
            details: this.binderObs$()
          });
        })
      )
      .subscribe(results => {
        this.setBinder(results.details);
        this.binder.set(results.binders.find((b: any) => b.ident==this.uBinder().binder));
        if(this.needWatch(this.scans()))
          this.startWatchBinder();

        //modification du menu
        this.menu.setAction('page', this.uBinder().ident);

        this.loading.set(false);
        if(this.fragment)
          setTimeout(() => document.getElementById(this.fragment)?.scrollIntoView({ behavior: 'instant', block: 'center' }), 200);
      });
  }

  ngOnDestroy(){
    this.menu.setAction('binder');
    this.endWatchBinder();
  }

  updatePages(val: PageApiResponse){
    //ajout ou remplacement de la nouvelle page dans la liste des pages existantes
    const i: number=this.pages().findIndex(p => p.index==val.page.index);
    let pages: Page[]=[];
    let scans: Scan[]=[];
    if(i!=-1){
      //supression des cartes 
      this.cards.set(this.cards().filter(c => c.page!=this.pages()[i].ident));

      //remplacement de la page
      this.pages().splice(i, 1, val.page);
      pages=[...this.pages()];

      //remplacement du scan
      this.scans().splice(this.scans().findIndex(s => s.ident==this.pages()[i].scan), i, val.scan);
      scans=[...this.scans()];
    }
    else{
      pages=[...this.pages(), val.page];
      scans=[...this.scans(), val.scan];
    }

    //tris par index
    this.pages.set(pages.sort((a, b) => a.index-b.index));
    this.scans.set(scans);

    this.startWatchBinder();
  }

  updateCard(val: CardApiResponse){
    this.cards.set([...this.cards(), val.card]);
    this.scans.set([...this.scans(), val.scan]);
    this.startWatchBinder();
  }

  setBinder(details: any){
    this.uBinder.set(details.uBinder);
    this.pages.set(details.pages);
    this.cards.set(details.cards);
    this.references.set(details.references);
    this.scans.set(details.scans);
  }

  needWatch(scans: Scan[]){
    return scans.filter(s => (s.state=='waiting' || s.state=='queued')).length>0;
  }

  startWatchBinder(){
    this.endWatchBinder();
    this.timeout=setTimeout(() => this.watchBinder(), 20000);
  }

  //recupération des informations du binder afin de mettre à jour les pages / cartes
  watchBinder(){
    this.binderObs$()
      .subscribe(details => {
        this.updateDetails(details);
        if(this.needWatch(details.scans))
          this.startWatchBinder();
      });
  }

  //recherche les différences et notifie
  //modifie les pages/cartes
  updateDetails(details: any){
    const last=JSON.stringify(this.scans());
    const current=JSON.stringify(details.scans);

    if(last!=current)
      this.notifService.notifySuccess('Binder mis à jour avec les cartes analysées.');

    //modification des datas
    this.setBinder(details);
  }

  endWatchBinder(){
    if(this.timeout)
      clearTimeout(this.timeout);
  }
}
